import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Modal, Select, Space, Typography, message } from "antd";
import apiClient from "../../api/apiClient";
import API_CONFIG from "../../config/api";
import useBusinessResource from "../../hooks/useBusinessResource";
import { useStoreConfigurationScope } from "../useStoreConfigurationResources";
import { storeRequestError } from "../storeConfigurationModel";

const fingerprint = (data) => JSON.stringify([data.schema_ready, data.supplier_id, data.configured_supplier_unavailable,
  (data.suppliers || []).map((supplier) => [Number(supplier.id), supplier.name])]);
export default function StoreDeliverySupplierSettings({ storeId, onDirtyChange, onBusyChange }) {
  const { scope, headers, permissions } = useStoreConfigurationScope();
  const [notices, noticeContext] = message.useMessage();
  const [dialog, dialogContext] = Modal.useModal();
  const [baseline, setBaseline] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState("");
  const mounted = useRef(true);
  const savingRef = useRef(false);
  const abort = useRef(null);
  const resource = useBusinessResource({ ...scope, resource: "store-configuration-delivery", parameters: [storeId],
    enabled: permissions.canViewStores(), active: true, load: async (signal) => {
      const { data } = await apiClient.get(API_CONFIG.apiUrl(`/ecommerce/stores/${storeId}/delivery-settings`), { headers, signal });
      if (!Array.isArray(data.suppliers)) throw new Error("Les prestataires sont indisponibles.");
      return data;
    },
  });
  const apply = (data) => { setBaseline(data); setSupplier(data.supplier_id ? Number(data.supplier_id) : null); setDirty(false); dirtyRef.current = false; setConflict(false); setError(""); };
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; abort.current?.abort(); };
  }, []);
  useEffect(() => {
    if (!resource.data || savingRef.current) return;
    if (!dirtyRef.current) {
      setBaseline(resource.data); setSupplier(resource.data.supplier_id ? Number(resource.data.supplier_id) : null); setConflict(false);
    } else if (baseline && fingerprint(resource.data) !== fingerprint(baseline)) setConflict(true);
  }, [resource.data, baseline]);
  useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => { onBusyChange?.(saving); }, [saving, onBusyChange]);
  const reload = () => {
    const run = () => resource.refresh().then((data) => { if (mounted.current) apply(data); }).catch(() => {});
    if (!dirty) void run();
    else dialog.confirm({ title: "Recharger le prestataire enregistré ?", content: "Votre sélection non enregistrée sera abandonnée.",
      okText: "Recharger", cancelText: "Conserver ma sélection", onOk: run });
  };
  const save = async () => {
    if (savingRef.current || !permissions.canEditStores() || !baseline?.schema_ready || resource.error || conflict || !dirty) return;
    savingRef.current = true; setSaving(true); setError("");
    const controller = new AbortController(); abort.current = controller;
    try {
      const latest = await resource.refresh();
      if (!mounted.current || controller.signal.aborted) return;
      if (!latest.schema_ready || fingerprint(latest) !== fingerprint(baseline)
        || (supplier && !latest.suppliers.some((item) => Number(item.id) === supplier))) { setConflict(true); return; }
      await apiClient.put(API_CONFIG.apiUrl(`/ecommerce/stores/${storeId}/delivery-settings`), { supplier_id: supplier || null }, { headers, signal: controller.signal });
      if (!mounted.current || controller.signal.aborted) return;
      const saved = await resource.refresh();
      if (mounted.current) { apply(saved); notices.success("Prestataire par défaut enregistré."); }
    } catch (cause) { if (mounted.current && !controller.signal.aborted) setError(storeRequestError(cause, "Le prestataire n'a pas pu être enregistré ou actualisé. Votre sélection est conservée.")); }
    finally { if (mounted.current) { savingRef.current = false; setSaving(false); } }
  };
  const data = resource.data;
  const options = (data?.suppliers || []).map((item) => ({ value: Number(item.id), label: item.name }));
  if (supplier && !options.some((item) => item.value === supplier)) options.push({ value: supplier, label: "Prestataire indisponible", disabled: true });
  const reason = !permissions.canEditStores() ? "La modification de cette boutique n'est pas autorisée."
    : resource.error ? "Actualisez les prestataires avant d'enregistrer." : conflict ? "Rechargez la configuration modifiée avant de poursuivre."
    : !data ? "Chargement des prestataires." : !data.schema_ready ? "Le suivi financier des livraisons doit être activé par un administrateur."
    : !dirty ? "Aucune modification du prestataire en attente." : "Le prestataire s'enregistre séparément des informations de la boutique.";
  return <section aria-label="Prestataire de livraison par défaut">
    {noticeContext}{dialogContext}
    <Typography.Title level={5}>Prestataire de livraison par défaut</Typography.Title>
    <Typography.Paragraph type="secondary">Préremplit le fournisseur sur les commandes. Le coût réel et le paiement sont confirmés pour chaque livraison.</Typography.Paragraph>
    {resource.error && <Alert type="error" showIcon message="Les prestataires n'ont pas pu être actualisés. Votre sélection est conservée."
      action={<Button onClick={() => void resource.refresh().catch(() => {})}>Réessayer</Button>} />}
    {error && <Alert type="error" showIcon message={error} />}
    {conflict && <Alert type="warning" showIcon message="La configuration ou les prestataires ont changé depuis une autre session."
      description="Votre sélection est conservée." action={<Button onClick={reload}>Recharger les valeurs</Button>} />}
    {data?.configured_supplier_unavailable && <Alert type="warning" showIcon message="Le prestataire enregistré est indisponible. Choisissez un prestataire actif ou désactivez ce réglage." />}
    <Space direction="vertical" style={{ width: "100%" }}>
      <Select aria-label="Prestataire par défaut" allowClear showSearch optionFilterProp="label" placeholder="Aucun prestataire par défaut"
        style={{ width: "100%" }} value={supplier || undefined} loading={resource.loading && !data}
        disabled={!data?.schema_ready || saving || !permissions.canEditStores()} options={options}
        onChange={(value) => { setSupplier(value || null); setDirty(true); dirtyRef.current = true; }} />
      <Button data-testid="store-config-delivery-save" onClick={() => void save()} loading={saving}
        disabled={!data?.schema_ready || !permissions.canEditStores() || Boolean(resource.error) || conflict || !dirty}>Enregistrer le prestataire</Button>
      <p className="store-config-help">{reason}</p>
    </Space>
  </section>;
}
